import { useState, useRef, useEffect } from 'react';
import { Map as MapGL, NavigationControl, GeolocateControl, type MapRef } from 'react-map-gl/mapbox';
import { CustomFullscreenControl } from './components/CustomFullscreenControl';
import 'mapbox-gl/dist/mapbox-gl.css';
import SiteMarker from './components/SiteMarker';
import MapPopup from './components/MapPopup';
import type { FleetSiteMapPoint } from './components/SiteMarker';
import { FilterBar } from '../FilterBar';
import type { ColumnFiltersState, QuickFilterConfig } from '../FilterBar';
import type { Updater } from '@tanstack/react-table';
import * as mapStyles from './Map.style.css';
import { useT } from "../../../app/lib/i18n";

export type { FleetSiteMapPoint } from './components/SiteMarker';

export interface MapProps {
  /** Filter bar props - when provided, FilterBar is shown above the map */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (updater: Updater<ColumnFiltersState>) => void;
  quickFilters?: QuickFilterConfig[];
  searchPlaceholder?: string;
  /** Points to display as status-colored markers. Always sourced from the caller — never mocked. */
  sites?: FleetSiteMapPoint[];
  /** Called when "View details" is clicked in a marker's popup. */
  onSiteClick?: (site: FleetSiteMapPoint) => void;
  /** When true, map fills container (100% width/height) and calls resize() on container change. Use in dashboard widgets. */
  fillContainer?: boolean;
  /** Initial map center longitude. */
  initialLongitude?: number;
  /** Initial map center latitude. */
  initialLatitude?: number;
  /** Initial zoom level. */
  initialZoom?: number;
}


const MAPBOX_TOKEN = import.meta.env.VITE_MAP_BOX_TOKEN;

function isWebGLSupported(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
    return Boolean(gl);
  } catch {
    return false;
  }
}

const Map = ({
  columnFilters,
  onColumnFiltersChange,
  quickFilters,
  searchPlaceholder = 'Search...',
  sites = [],
  onSiteClick,
  fillContainer = false,
  initialLongitude = -119.5,
  initialLatitude = 36.8,
  initialZoom = 6,
}: MapProps = {}) => {
  const t = useT();
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [viewState, setViewState] = useState({
    longitude: initialLongitude,
    latitude: initialLatitude,
    zoom: initialZoom,
  });
  const [mapError, setMapError] = useState<Error | null>(null);
  const [scrollZoom, setScrollZoom] = useState(false);
  const [selectedSite, setSelectedSite] = useState<FleetSiteMapPoint | null>(null);

  useEffect(() => {
    if (!fillContainer || !containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const ref = mapRef.current as { getMap?: () => { resize: () => void }; resize?: () => void } | null;
      if (ref?.resize) ref.resize();
      else ref?.getMap?.()?.resize?.();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillContainer]);

  const showFilterBar =
    quickFilters &&
    quickFilters.length > 0 &&
    columnFilters !== undefined &&
    onColumnFiltersChange !== undefined;

  const mapPlaceholder = (message: string) => (
    <div className={mapStyles.mapWrapper} style={{ width: '100%', height: '100%', minHeight: 200 }}>
      {showFilterBar && (
        <FilterBar
          columnFilters={columnFilters!}
          onColumnFiltersChange={onColumnFiltersChange!}
          quickFilters={quickFilters!}
          searchPlaceholder={searchPlaceholder}
        />
      )}
      <div
        className={mapStyles.mapContainer}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#e5e7eb',
          color: '#6b7280',
          fontSize: 14,
        }}
      >
        {message}
      </div>
    </div>
  );

  if (!MAPBOX_TOKEN) {
    return mapPlaceholder('Map requires VITE_MAP_BOX_TOKEN (set in .env or CI secrets).');
  }
  if (!isWebGLSupported()) {
    return mapPlaceholder('Map could not be loaded (WebGL is not supported in this environment).');
  }
  if (mapError) {
    return mapPlaceholder(`Map could not be loaded (${mapError.message || 'WebGL failed'}).`);
  }

  const wrapperStyle = fillContainer
    ? { width: '100%' as const, height: '100%' as const }
    : { width: 600, height: 400 };

  return (
    <div ref={fullscreenContainerRef} className={mapStyles.mapWrapper} style={wrapperStyle}>
      {showFilterBar && (
        <FilterBar
          columnFilters={columnFilters!}
          onColumnFiltersChange={onColumnFiltersChange!}
          quickFilters={quickFilters!}
          searchPlaceholder={searchPlaceholder}
        />
      )}
      <div ref={containerRef} className={mapStyles.mapContainer}
        onClick={() => setScrollZoom(true)}
        onMouseLeave={() => setScrollZoom(false)}
      >
        {!scrollZoom && (
          <div className={mapStyles.scrollHint}>{t("shared.map.enableScrollZoom")}</div>
        )}
      <MapGL
        ref={mapRef}
        reuseMaps={true}
        mapboxAccessToken={MAPBOX_TOKEN}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onError={(evt) => {
          const err = evt.error;
          setMapError(err instanceof Error ? err : new Error(String(err)));
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        scrollZoom={scrollZoom}
      >
        <CustomFullscreenControl containerRef={fullscreenContainerRef} position="top-right" />
        <NavigationControl position="top-left" />
        <GeolocateControl position="top-left" />
        {sites.map((site) => (
          <SiteMarker key={site.id} site={site} onClick={setSelectedSite} />
        ))}
        {selectedSite && (
          <MapPopup
            site={selectedSite}
            onClose={() => setSelectedSite(null)}
            onViewDetails={(site) => {
              onSiteClick?.(site);
              setSelectedSite(null);
            }}
          />
        )}
      </MapGL>
      </div>
    </div>
  )
};

export { Map };
export default Map;